import React, { useEffect, useRef, useState } from "react"
import { FormGroup, InputAdornment, MenuItem, Select, Tab, Tabs, TextField } from "@mui/material"
import { toast } from "react-toastify"
import styles from "../../assets/styles/appointments.module.css"
import CustomContainer from "../../components/common/CustomContainer"
import Loader from "../../components/common/Loader"
import { CustomTabPanel, a11yProps } from "../../components/common/CustomTabPanel"
import LabTestRequestTable from "../../components/labTestRequest/LabTestRequestTable"
import LabStatusChart from "../../components/labTestRequest/LabStatusChart"
import { getLabTestsApi } from "../../utils/api/services"
import { SearchIcon } from "../../utils/functions/Icons"

// status values understood by /hr/labTests, same as the care navigator portal
const labTestTabs = [
    { label: 'Requested', status: 'pending' },
    { label: 'Scheduled', status: 'confirmed' },
    { label: 'Cancelled', status: 'cancelled' },
    { label: 'Collected', status: 'Completed' },
    { label: 'Report received', status: 'Report Generated' },
]

const LabTestRequest = () => {
    const [value, setValue] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [filter, setFilter] = useState('0');
    const [loading, setLoading] = useState(false);
    const [labTests, setLabTests] = useState<LabTestRequestTypes[]>([]);
    const [labTestSummary, setLabTestSummary] = useState<LabTestSummaryTypes | null>(null);
    const lastRequest = useRef('');

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    // one call returns the rows for the tab and the status summary for the graph
    const fetchLabTests = async (data: { status: string, search: string, filter: string }) => {
        // StrictMode runs effects twice in development, skip a repeat of the request just sent
        const requestKey = JSON.stringify(data);
        if (lastRequest.current === requestKey) return;
        lastRequest.current = requestKey;
        try {
            setLoading(true)
            const result = await getLabTestsApi(data);
            if (result?.success) {
                setLabTests(result?.labTests || [])
                setLabTestSummary(result?.labTestSummary || null)
            }
            setLoading(false)
        } catch (error: any) {
            lastRequest.current = '';
            setLoading(false)
            setLabTests([])
            toast.error(error?.data?.errors || error?.message);
        }
    }

    useEffect(() => {
        const data = { status: labTestTabs[value].status, search: searchValue, filter: filter === '0' ? '' : filter };
        if (searchValue) {
            const cleanFunction = setTimeout(() => {
                fetchLabTests(data);
            }, 1000);
            return () => clearTimeout(cleanFunction);
        } else {
            fetchLabTests(data);
        }
    }, [value, searchValue, filter]);

    return (
        <CustomContainer>
            <div className='AllPageMain'>
                <div className='HeadAndButton'>
                    <h2>Health Test Request</h2>
                </div>
                {labTestSummary && (
                    <div className="my-6">
                        <LabStatusChart
                            summary={labTestSummary}
                            selectedStatus={labTestTabs[value].status}
                            onSelectStatus={(status) => {
                                const index = labTestTabs.findIndex((tab) => tab.status === status);
                                if (index !== -1) setValue(index);
                            }}
                        />
                    </div>
                )}
                <div className={styles.appointmentsMain}>
                    <Tabs value={value} onChange={handleChange} className="custumTabs">
                        {labTestTabs.map((tab, index) => (
                            <Tab key={tab.status} label={tab.label} {...a11yProps(index)} />
                        ))}
                    </Tabs>
                    {labTestTabs.map((tab, index) => (
                        <CustomTabPanel key={tab.status} value={value} index={index}>
                            <div className="mb-6 flex justify-between items-center flex-col md:flex-row w-full max-w-full gap-y-4 md:gap-y-0">
                                <FormGroup className="customFormGroup md:w-1/2 w-full">
                                    <TextField
                                        className={`custumTextField ${styles.InputStyle}`}
                                        id="searchValue"
                                        name="searchValue"
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        value={searchValue}
                                        placeholder="Search by employee, lab or city"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormGroup>
                                <FormGroup className="customFormGroup">
                                    <Select
                                        className="custumTextField profileInput"
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                    >
                                        <MenuItem value="0">All</MenuItem>
                                        <MenuItem value="1">Last Month</MenuItem>
                                        <MenuItem value="2">6 Months</MenuItem>
                                        <MenuItem value="3">1 Year</MenuItem>
                                    </Select>
                                </FormGroup>
                            </div>
                            {
                                loading ? <Loader /> :
                                    <LabTestRequestTable labTests={labTests} />
                            }
                        </CustomTabPanel>
                    ))}
                </div>
            </div>
        </CustomContainer>
    )
}

export default LabTestRequest
